import { CheckCircleIcon } from '@chakra-ui/icons';
import {
  Badge,
  Button,
  Card,
  HStack,
  Icon,
  Skeleton,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline';
import {
  SortingState,
  Updater,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { SubscriptionsTabsStatus, noop } from '@blockpulse3/data/shared';
import {
  FundraisingStatus,
  IdentityType,
  SubscriptionInfosFragment,
} from '@blockpulse3/graphql/hooks';
import { formatDate, formatNumberCurrency, formatNumberPercentage } from '@blockpulse3/helpers';
import {
  IdentityAvatar,
  PaginationButtons,
  ProgressStep,
  TableContainer,
} from '@blockpulse3/ui/commons';
import {
  PaginationHandlers,
  useInvestStatusBadge,
  useStepStatusMapLazy,
} from '@blockpulse3/ui/ui-hooks';

import { FundraisingSubscribersTableEmpty } from './FundraisingSubscribersTableEmpty';

type Props = PaginationHandlers & {
  fundraisingStatus: FundraisingStatus | undefined;
  tabName: SubscriptionsTabsStatus;
  onRowClick?: (id: SubscriptionInfosFragment['id']) => void;
  sorting: SortingState;
  onSortingChange: (sortFn: Updater<SortingState>) => void;
};

export function FundraisingSubscribersTable({
  fundraisingStatus,
  tabName,
  results,
  sorting,
  pageInfo,
  pageCount,
  currentPage,
  loading,
  isLoadingMore,
  handleNextPage,
  handlePreviousPage,
  onSortingChange,
  onRowClick = noop,
}: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nFundraisingSubscribersTable = useTranslations('FundraisingSubscribersTable');

  const { fundraisingId = '' } = useParams();

  /* ** Function to return subscription invest status badge ** */
  const investStatusBadge = useInvestStatusBadge();

  const generateStepStatusMap = useStepStatusMapLazy();

  const columnHelper = useMemo(() => createColumnHelper<SubscriptionInfosFragment>(), []);
  const columns = useMemo(
    () => [
      columnHelper.accessor('identity', {
        header: i18nFundraisingSubscribersTable('SubscriberName'),
        enableSorting: false,
        cell: (info) => {
          const identity = info.getValue();

          return (
            <Td>
              <HStack spacing="2.5">
                <IdentityAvatar identity={identity} />
                <Stack spacing="1">
                  <HStack spacing="1">
                    <Text fontWeight="600">{identity.name}</Text>
                    {identity.isVerified && (
                      <Icon as={CheckCircleIcon} boxSize="15px" color="green.400" />
                    )}
                    {identity.type === IdentityType.COMPANY && (
                      <Text fontWeight="500">({identity.identifier})</Text>
                    )}
                  </HStack>
                  <Text color="gray.500" fontWeight="400">
                    {identity.email}
                  </Text>
                </Stack>
              </HStack>
            </Td>
          );
        },
      }),
      columnHelper.accessor('tags', {
        header: 'Tags',
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <Td>
              <HStack flexWrap="wrap" minWidth="150px" spacing="2">
                {row.original?.tags?.map((tag) => {
                  return (
                    <Badge key={tag.id} colorScheme={tag.color}>
                      {tag.name}
                    </Badge>
                  );
                })}
              </HStack>
            </Td>
          );
        },
      }),
      columnHelper.accessor('investStatus', {
        header: i18nFundraisingSubscribersTable('Status'),
        enableSorting: false,
        cell: ({ row }) => {
          const subscriber: SubscriptionInfosFragment = row.original;
          const badge = investStatusBadge(subscriber, fundraisingStatus);
          return (
            <Td>
              <Badge colorScheme={badge.color}>{badge.label}</Badge>
            </Td>
          );
        },
      }),
      columnHelper.accessor('stepStatuses', {
        header: i18nFundraisingSubscribersTable('Steps'),
        enableSorting: false,
        cell: ({ row }) => {
          const subscriber: SubscriptionInfosFragment = row.original;
          const [, steps] = generateStepStatusMap(subscriber.data?.mergedSteps);
          return (
            <Td>
              <HStack>
                {steps.map((step) => (
                  <Tooltip key={step.index} hasArrow label={step.title} placement="top">
                    <Button minW="0" variant="link">
                      <ProgressStep
                        index={step.index}
                        status={step.status}
                        textProps={{ fontSize: 'sm', fontWeight: '500', color: 'gray.500' }}
                      />
                    </Button>
                  </Tooltip>
                ))}
              </HStack>
            </Td>
          );
        },
      }),
      columnHelper.accessor('investAmount', {
        header: i18nFundraisingSubscribersTable('InvestAmount'),
        enableSorting: false,
        cell: (info) => (
          <Td isNumeric fontWeight="600">
            {info.getValue() === 0 ? '-' : formatNumberCurrency(info.getValue())}
          </Td>
        ),
      }),
      columnHelper.accessor('admissionFees', {
        header: i18nFundraisingSubscribersTable('AdmissionFees'),
        enableSorting: false,
        cell: (info) => (
          <Td isNumeric fontWeight="600">
            {info.getValue() ? formatNumberPercentage(info.getValue()) : '-'}
          </Td>
        ),
      }),
      columnHelper.accessor('completedAt', {
        header: i18nFundraisingSubscribersTable('CompletedAt'),
        enableSorting: true,
        cell: (info) => (
          <Td align="right" fontWeight="600">
            {info.getValue() ? formatDate(info.getValue(), 'DD/MM/YYYY HH:mm:ss') : '-'}
          </Td>
        ),
      }),
      columnHelper.accessor('id', {
        header: '',
        enableSorting: false,
        cell: (info) => (
          <Td key={info.cell.id} align="right" textAlign="right">
            <Button variant="secondary">{i18nGeneral('DetailsButton')}</Button>
          </Td>
        ),
      }),
    ],
    [i18nGeneral, i18nFundraisingSubscribersTable, columnHelper, investStatusBadge],
  );
  const numericColumns = ['investAmount', 'admissionFees'];

  const table = useReactTable({
    data: (results.length ? results : []) as SubscriptionInfosFragment[],
    columns,
    state: { sorting },
    onSortingChange: (sortFn) => {
      onSortingChange(sortFn);
    },
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    table.setColumnVisibility({
      stepStatuses: tabName === SubscriptionsTabsStatus.PENDING,
      completedAt: tabName === SubscriptionsTabsStatus.COMPLETED,
    });
  }, [tabName, table]);

  const handleRowClick = (id: SubscriptionInfosFragment['id']): void => {
    onRowClick(id);
  };

  if (loading || !fundraisingId) {
    return (
      <Skeleton>
        <Card h="400px" />
      </Skeleton>
    );
  }

  if (!isLoadingMore && !results.length) {
    return <FundraisingSubscribersTableEmpty />;
  }

  return (
    <Stack spacing="4">
      <TableContainer maxH="850px">
        <Table variant="striped">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    cursor={header.column.getCanSort() ? 'pointer' : 'initial'}
                    isNumeric={numericColumns.includes(header.id)}
                    w={header.id === 'identity' ? '40%' : '15%'}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <HStack spacing="0">
                      <Text>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </Text>
                      {header.column.getIsSorted() && (
                        <Icon
                          boxSize="16px"
                          as={
                            header.column.getIsSorted() === 'asc' ? ChevronUpIcon : ChevronDownIcon
                          }
                        />
                      )}
                    </HStack>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {isLoadingMore ? (
              <Tr key="0">
                <Td colSpan={5}>
                  <Stack alignItems="center" h="737px" justifyContent="center">
                    <Spinner />
                  </Stack>
                </Td>
              </Tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <Tr
                  key={row.original.id}
                  role="button"
                  onClick={(): void => handleRowClick(row.original.id)}
                >
                  {row.getVisibleCells().map((cell) =>
                    flexRender(cell.column.columnDef.cell, {
                      ...cell.getContext(),
                      key: cell.id,
                    }),
                  )}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <PaginationButtons
        currentPage={currentPage}
        hasNextPage={pageInfo?.hasNextPage}
        hasPreviousPage={pageInfo?.hasPreviousPage}
        loading={isLoadingMore}
        pageCount={pageCount}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
      />
    </Stack>
  );
}
